import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Input, PasswordInput } from "@-/dev.improve.haniverse.components.ui.input";
import { Label } from "@-/dev.improve.haniverse.components.ui.label";
import { Separator } from "@-/dev.improve.haniverse.components.ui.separator";
import { Surface, SurfaceContent } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Pressable, type TextInput, View } from "@-/com.npmjs.react-native";
import { useKeyboardState } from "@-/com.npmjs.react-native-keyboard-controller";
import * as React from "react";

/**
 * The standalone card's fields keep the Input's own border, fill and shadow. Input's web base
 * steps down to `sm:h-9` on wider windows, so the card pins one height beside its 48pt buttons.
 */
const CARD_FIELD_CLASS_NAME = "h-12 rounded-xs bg-popover px-4 text-base sm:h-12 sm:leading-12 md:text-base";

export type AuthFormProps = {
  /**
   * Renders only the email + password fields, the submit button and the forgot-password link:
   * no headings, social connections or footer toggle. For hosts that own that chrome (the auth
   * sheet's Log In / Sign Up toggle and provider buttons).
   */
  bare?: boolean;
  /** Disables the inputs and primary button while an auth request is in flight. */
  busy?: boolean;
  /** Centred heading and a divided footer for a host's standalone auth card. */
  card?: boolean;
  /** Optional className override for the outer `Surface`. */
  className?: string;
  /** Single-screen layout for hosts that already provide login/sign-up tabs. */
  compact?: boolean;
  /** Optional className override for the `SurfaceContent`. */
  contentClassName?: string;
  description?: string;
  /** Controlled email value; falls back to internal uncontrolled state when omitted. */
  email?: string;
  /** Footer link label, e.g. `"Sign in"`. */
  footerLinkLabel: string;
  /**
   * Footer prompt rendered before the navigate link, e.g.
   * `"Already have an account?"`.
   */
  footerPrompt: string;
  onChangeEmail?: (value: string) => void;
  onChangePassword?: (value: string) => void;
  /**
   * When provided, renders a "Forgot your password?" link beside the password
   * label. Omit to hide it.
   */
  onForgotPassword?: () => void;
  /** Invoked when the footer navigate link is pressed. */
  onNavigate?: () => void;
  /** Submit handler. Receives the current field values. */
  onSubmit?: (values: { email: string; password: string }) => void;
  /** Controlled password value; falls back to internal uncontrolled state when omitted. */
  password?: string;
  slogan?: string;
  /**
   * Optional social sign-in controls (e.g. Apple / Google buttons). Omit or pass
   * `null` to hide social sign-in entirely.
   */
  socialConnections?: React.ReactNode;
  submitLabel?: string;
  /** Replaces the primary button label while this specific submission is pending. */
  submitPendingLabel?: string;
  /** Heading introducing the form, e.g. `"Welcome back"`. */
  title: string;
};

/**
 * Shared email/password auth form used by both `SignInForm` and `SignUpForm`.
 * The differing bits (title, footer copy, the forgot-password link, and surface
 * styling) are passed in so each caller keeps its own public API.
 */
export function AuthForm({
  bare = false,
  busy = false,
  card = false,
  className = "overflow-visible rounded-none border-0 bg-transparent",
  compact = false,
  contentClassName = "gap-6",
  description = "A little practice. A world of possibility.",
  email: emailProp,
  footerLinkLabel,
  footerPrompt,
  onChangeEmail,
  onChangePassword,
  onForgotPassword,
  onNavigate,
  onSubmit,
  password: passwordProp,
  slogan = "Master Chinese, fast.",
  socialConnections,
  submitLabel = "Continue",
  submitPendingLabel,
  title,
}: AuthFormProps) {
  const passwordInputRef = React.useRef<TextInput>(null);
  const isKeyboardVisible = useKeyboardState((state) => state.isVisible);
  const isEditingCompactForm = compact && isKeyboardVisible;

  const [emailState, setEmailState] = React.useState("");
  const [passwordState, setPasswordState] = React.useState("");
  const email = emailProp ?? emailState;
  const password = passwordProp ?? passwordState;

  function handleChangeEmail(value: string) {
    if (onChangeEmail != null) {
      onChangeEmail(value);
    } else {
      setEmailState(value);
    }
  }

  function handleChangePassword(value: string) {
    if (onChangePassword != null) {
      onChangePassword(value);
    } else {
      setPasswordState(value);
    }
  }

  function onEmailSubmitEditing() {
    passwordInputRef.current?.focus();
  }

  function handleSubmit() {
    onSubmit?.({ email, password });
  }

  if (bare) {
    // Only the fields: the auth sheet supplies the mode toggle and the provider buttons, and
    // reveals this form from its email link. Every row is one 48pt line with a 12pt gap, so the
    // sheet can reserve identical space in both modes.
    return (
      <View className="gap-3">
        <Input
          accessibilityLabel="Email address"
          autoCapitalize="none"
          autoComplete="email"
          autoFocus={email.length === 0}
          editable={!busy}
          keyboardType="email-address"
          onChangeText={handleChangeEmail}
          onSubmitEditing={onEmailSubmitEditing}
          placeholder="Email address"
          returnKeyType="next"
          submitBehavior="submit"
          testID="auth-email-field"
          value={email}
        />
        <PasswordInput
          accessibilityLabel="Password field"
          autoFocus={email.length > 0}
          editable={!busy}
          onChangeText={handleChangePassword}
          onSubmitEditing={handleSubmit}
          placeholder="Password"
          ref={passwordInputRef}
          returnKeyType="send"
          testID="auth-password-field"
          value={password}
        />
        <Button
          accessibilityLabel="Submit auth form"
          accessibilityState={{ busy: submitPendingLabel != null, disabled: busy }}
          className="h-12 w-full rounded-full"
          disabled={busy}
          onPress={handleSubmit}
        >
          <Text>{submitPendingLabel ?? submitLabel}</Text>
        </Button>
        {onForgotPassword == null ? null : (
          <Button
            className="h-12"
            disabled={busy}
            onPress={() => {
              onForgotPassword();
            }}
            testID="auth-forgot-password"
            variant="link"
          >
            <Text className="font-normal text-muted-foreground">Forgot your password?</Text>
          </Button>
        )}
      </View>
    );
  }

  return (
    <View className="gap-8">
      {!compact && !card ? (
        <View className="gap-5">
          <View className="flex-row items-center gap-2">
            <View className="size-2 rounded-full bg-amber-500" />
            <Text className="text-2xl font-bold tracking-tight">Han</Text>
          </View>
          <View className="gap-3">
            <Text className="text-4xl leading-11 font-bold tracking-tight" role="heading">
              {slogan}
            </Text>
            <Text className="text-base leading-6 text-muted-foreground">{description}</Text>
          </View>
        </View>
      ) : null}
      <Surface className={className ?? "overflow-visible rounded-none border-0 bg-transparent"}>
        <SurfaceContent
          className={
            card ? "gap-6" : isEditingCompactForm ? "gap-3" : compact ? "gap-4" : contentClassName
          }
        >
          <View className={card ? "gap-1.5" : "gap-1"}>
            <Text
              className={
                card
                  ? "text-center text-xl leading-7 font-semibold tracking-tight"
                  : compact
                    ? "text-2xl leading-8 font-semibold tracking-tight"
                    : "text-xl font-semibold tracking-tight"
              }
              role="heading"
            >
              {title}
            </Text>
            {card || (compact && !isEditingCompactForm) ? (
              <Text
                className={
                  card
                    ? "text-center text-sm leading-5 text-muted-foreground"
                    : "text-sm leading-5 text-muted-foreground"
                }
              >
                {description}
              </Text>
            ) : null}
          </View>
          {socialConnections == null || isEditingCompactForm ? null : (
            <View className={compact ? "gap-3" : "gap-5"}>
              {socialConnections}
              <View className="flex-row items-center gap-4">
                <Separator className={card ? "flex-1 bg-border" : "flex-1 bg-border/50"} />
                <Text className="text-xs text-muted-foreground">or continue with email</Text>
                <Separator className={card ? "flex-1 bg-border" : "flex-1 bg-border/50"} />
              </View>
            </View>
          )}
          <View className={card ? "gap-4" : compact ? "gap-3" : "gap-6"}>
            <View className={card ? "gap-2" : compact ? "gap-1" : "gap-1.5"}>
              <Label htmlFor="email">Email</Label>
              <Input
                // Label the INPUT itself "Email address" so automation focuses the
                // field directly. The visible <Label> reads "Email" too, so a bare
                // "Email" tap is ambiguous (and tends to land on the Label, which
                // doesn't focus the input). A distinct input label disambiguates.
                accessibilityLabel="Email address"
                autoCapitalize="none"
                autoComplete="email"
                className={
                  card
                    ? CARD_FIELD_CLASS_NAME
                    : compact
                      ? "h-12 rounded-xs border-border/50 bg-muted/40 px-4 text-base"
                      : "h-14 rounded-xs border-border/50 bg-muted/40 px-4 text-base"
                }
                editable={!busy}
                id="email"
                keyboardType="email-address"
                onChangeText={handleChangeEmail}
                onSubmitEditing={onEmailSubmitEditing}
                placeholder="hello@example.com"
                returnKeyType="next"
                submitBehavior="submit"
                // Stable id for E2E — maps to the iOS accessibilityIdentifier, queryable
                // via Maestro's `id:` selector even when the accessibilityLabel is not.
                testID="auth-email-field"
                value={email}
              />
            </View>
            <View className={card ? "gap-2" : compact ? "gap-1" : "gap-1.5"}>
              {onForgotPassword == null ? (
                <Label htmlFor="password">Password</Label>
              ) : (
                <View className="flex-row items-center">
                  <Label htmlFor="password">Password</Label>
                  <Button
                    className="ml-auto h-4 px-1 py-0 web:h-fit"
                    onPress={() => {
                      onForgotPassword();
                    }}
                    size="sm"
                    variant="link"
                  >
                    <Text
                      className={
                        card
                          ? "text-sm leading-4 font-normal text-muted-foreground"
                          : "leading-4 font-normal"
                      }
                    >
                      Forgot your password?
                    </Text>
                  </Button>
                </View>
              )}
              <PasswordInput
                // See the email input: label the field itself so automation can
                // focus it directly (the password Input has no placeholder, so
                // there's otherwise no text to target on the field).
                accessibilityLabel="Password field"
                className={
                  card
                    ? CARD_FIELD_CLASS_NAME
                    : compact
                      ? "h-12 rounded-xs border-border/50 bg-muted/40 px-4 text-base"
                      : "h-14 rounded-xs border-border/50 bg-muted/40 px-4 text-base"
                }
                editable={!busy}
                id="password"
                onChangeText={handleChangePassword}
                onSubmitEditing={handleSubmit}
                ref={passwordInputRef}
                returnKeyType="send"
                // Stable id for E2E. iOS routinely OMITS a `secureTextEntry` field's
                // accessibilityLabel from the a11y tree (a security measure), so a Maestro
                // `tapOn: text: "Password field"` intermittently can't find it and the flow
                // can't focus the field to type the password (the observed staging sign-in
                // failure). The accessibilityIdentifier (from testID) is exposed regardless,
                // so an `id:` tap reliably focuses it.
                testID="auth-password-field"
                value={password}
              />
            </View>
            <Button
              accessibilityLabel="Submit auth form"
              accessibilityState={{ busy: submitPendingLabel != null, disabled: busy }}
              className={
                card
                  ? "mt-2 h-12 w-full rounded-xs sm:h-12"
                  : compact
                    ? "h-12 w-full rounded-xs"
                    : "h-14 w-full rounded-xs"
              }
              disabled={busy}
              onPress={handleSubmit}
            >
              <Text className={card ? "text-base font-semibold" : undefined}>
                {submitPendingLabel ?? submitLabel}
              </Text>
            </Button>
          </View>
          {/* The ENTIRE prompt + link is ONE Pressable, NOT a Pressable nested
              inside <Text>. Two reasons:
              1. A Pressable nested in <Text> renders inline on iOS/Android and its
                 hit target + accessibility node get absorbed by the parent Text,
                 so the link's `onPress` never reliably fires from automation
                 (Maestro taps the text region but the toggle doesn't switch).
              2. If the prompt and link are SEPARATE sibling nodes, a regex like
                 "Already have an account.*" matches BOTH, and Maestro taps the
                 (non-interactive) prompt instead of the link.
              Wrapping the whole row in the Pressable gives a single touchable
              node whose accessibility label is the full "<prompt> <link>" — one
              unambiguous tap target that doesn't collide with the page title
              (also "Sign in"). The link text uses a stronger weight to read as an action. */}
          {card || !compact ? (
            <Pressable
              accessibilityLabel={`${footerPrompt} ${footerLinkLabel}`}
              accessibilityRole="button"
              className={
                card
                  ? "-mx-8 -mb-8 min-h-16 flex-row flex-wrap items-center justify-center gap-1 border-t border-border bg-muted/30 px-6 py-4"
                  : "min-h-11 flex-row flex-wrap items-center justify-center gap-1"
              }
              onPress={() => {
                onNavigate?.();
              }}
              /* The row collapses its children into the single combined accessibility label above, so
               neither inner Text is independently addressable on Android — a flow asserting the bare
               prompt can never pass (staging run 34424816998, auth-entry-modes). Give the row a
               stable testID so both suites can target it the same way they target the fields. */
              testID="auth-footer-toggle"
            >
              <Text className="text-center text-sm text-muted-foreground">{footerPrompt}</Text>
              <Text className="text-sm font-semibold">{footerLinkLabel}</Text>
            </Pressable>
          ) : null}
        </SurfaceContent>
      </Surface>
    </View>
  );
}
